const diasSemana = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
const MAX_DIAS_POR_PAGINA = 3;

const layoutGrid = {
  hLineWidth: () => 0.5,
  vLineWidth: () => 0.5,
  hLineColor: () => "#999999",
  vLineColor: () => "#999999",
};

const layoutSinBorde = { hLineWidth: () => 0, vLineWidth: () => 0 };

const layoutSoloInferior = {
  hLineWidth: (i, node) => (i === node.table.body.length ? 0.5 : 0),
  vLineWidth: () => 0,
  hLineColor: () => "#aaaaaa",
};

function buildPageHeader(proceso, programa, empresa, logoBase64, daysChunk, chunkIdx, totalChunks) {
  const nombreEmpresa =
    empresa?.nombreComercial ||
    [empresa?.nombre, empresa?.apellidoPaterno, empresa?.apellidoMaterno]
      .filter(Boolean)
      .join(" ") || 
    "";

  const paginaTexto = totalChunks > 1 ? `  (${chunkIdx + 1} / ${totalChunks})` : "";
  const diasTexto = daysChunk.join("  /  ");

  // ── Fila superior: logo | nombre empresa | CHECK LIST + proceso ──────────
  const colLogo = logoBase64
    ? { border: [false, false, false, false], image: "logo", fit: [70, 55], alignment: "center", margin: [0, 2, 0, 2] }
    : { border: [false, false, false, false], text: "", margin: [0, 0, 0, 0] };

  const colEmpresa = {
    border: [false, false, false, false],
    stack: [
      { text: nombreEmpresa, style: "hdrEmpresa", alignment: "center" },
    ],
    margin: [0, 16, 0, 0],
  };

  const colChecklist = {
    border: [false, false, false, false],
    stack: [
      {
        columns: [
          { text: "", width: "*" },
          {
            text: `CHECK LIST${paginaTexto}`,
            style: "hdrCheckTitle",
            alignment: "right",
            width: "auto",
          },
        ],
      },
      { text: proceso.nombre || "—", style: "hdrProceso", alignment: "right" },
    ],
    margin: [0, 6, 0, 0],
  };

  const filaTop = {
    table: {
      widths: ["18%", "38%", "44%"],
      body: [[colLogo, colEmpresa, colChecklist]],
    },
    layout: layoutSinBorde,
  };

  // ── Fila inferior: campos del formulario ─────────────────────────────────
  const mkField = (label, value, borderRight = true) => ({
    stack: [
      { text: label, style: "hdrFieldLabel" },
      { text: value, style: "hdrFieldValue" },
    ],
    margin: [6, 4, 6, 4],
    border: [false, false, borderRight, false],
  });

  const filaInfo = {
    table: {
      widths: ["30%", "18%", "26%", "26%"],
      body: [[
        mkField("PROGRAMA", programa?.nombre || "—"),
        mkField("DÍAS", diasTexto),
        mkField("SEMANA", "________________________________"),
        mkField("UBICACIÓN", "________________________________", false),
      ]],
    },
    layout: {
      hLineWidth: (i) => (i === 0 || i === 1 ? 0.5 : 0),
      vLineWidth: (i, node) => (i > 0 && i < node.table.widths.length ? 0.5 : 0),
      hLineColor: () => "#bbbbbb",
      vLineColor: () => "#cccccc",
    },
    margin: [0, 6, 0, 0],
  };

  return { stack: [filaTop, filaInfo] };
}

function buildChunkPage(daysChunk, proceso, scheduleLabels, programa, empresa, logoBase64, chunkIdx, totalChunks) {
  const nDays = daysChunk.length;
  const nSched = scheduleLabels.length;
  const totalDataCols = nDays * nSched;

  // '*' deja que pdfmake distribuya el espacio restante automáticamente
  const widths = [190, ...Array(totalDataCols).fill("*")];

  // ── Encabezados de la tabla ───────────────────────────────────────────────
  const headerRow1 = [
    {
      text: "ACTIVIDAD A REALIZAR",
      rowSpan: 2,
      style: "gridActivityHeader",
      alignment: "center",
      margin: [0, 8, 0, 8],
    },
    ...daysChunk.flatMap((dia) => [
      { text: dia, colSpan: nSched, style: "gridDayHeader", alignment: "center", margin: [0, 4, 0, 4] },
      ...Array(nSched - 1).fill({}),
    ]),
  ];

  const headerRow2 = [
    {},
    ...daysChunk.flatMap(() =>
      scheduleLabels.map((label) => ({
        text: label,
        style: "gridSchedHeader",
        alignment: "center",
        margin: [0, 3, 0, 3],
      }))
    ),
  ];

  // ── Filas de puntos ───────────────────────────────────────────────────────
  const puntos = proceso.puntos || [];
  const emptyCell = { text: " ", style: "gridDataCell" };

  const dataRows =
    puntos.length > 0
      ? puntos.map((punto, i) => [
          { text: `${i + 1}. ${punto.nombre}`, style: "gridActivityCell", margin: [4, 3, 3, 3] },
          ...Array(totalDataCols).fill(emptyCell),
        ])
      : [[
          { text: "(Sin puntos registrados)", style: "gridActivityCell", italics: true, color: "#aaaaaa", margin: [4, 5, 3, 5] },
          ...Array(totalDataCols).fill(emptyCell),
        ]];

  // ── Filas de firma ────────────────────────────────────────────────────────
  const buildSigRow = (label) => [
    { text: label, style: "gridSigLabel", margin: [4, 4, 3, 4] },
    ...daysChunk.flatMap(() => [
      { colSpan: nSched, text: " ", style: "gridSigCell", margin: [0, 10, 0, 0] },
      ...Array(nSched - 1).fill({}),
    ]),
  ];

  return {
    stack: [
      buildPageHeader(proceso, programa, empresa, logoBase64, daysChunk, chunkIdx, totalChunks),
      {
        canvas: [{ type: "line", x1: 0, y1: 0, x2: 968, y2: 0, lineWidth: 1.5, lineColor: "#2c3e50" }],
        margin: [0, 6, 0, 6],
      },
      {
        table: {
          widths,
          body: [
            headerRow1,
            headerRow2,
            ...dataRows,
            buildSigRow("OPERADOR RESPONSABLE:"),
            buildSigRow("SUPERVISOR DE TURNO:"),
          ],
        },
        layout: layoutGrid,
      },
      // Observaciones
      {
        stack: [
          { text: "OBSERVACIONES SEMANALES:", style: "obsLabel", margin: [0, 8, 0, 2] },
          {
            table: {
              widths: ["100%"],
              body: [
                [{ text: " ", style: "obsLine" }],
                [{ text: " ", style: "obsLine" }],
                [{ text: " ", style: "obsLine" }],
              ],
            },
            layout: layoutSoloInferior,
          },
          {
            columns: [
              { text: "CREADOR RESPONSABLE: _______________________________", style: "obsFirma", width: "50%" },
              { text: "SUPERVISOR DE TURNO: ________________________________", style: "obsFirma", width: "50%" },
            ],
            margin: [0, 6, 0, 0],
          },
        ],
      },
    ],
  };
}

function buildChecklistSection(proceso, programa, empresa, logoBase64) {
  const reglas = proceso.reglasProgramacion || {};

  // Días activos
  const rawDias = reglas.diasActivos || [];
  let activeDays = diasSemana
    .map((dia, i) => ({ dia, active: rawDias[i] }))
    .filter((x) => x.active)
    .map((x) => x.dia);
  if (activeDays.length === 0) activeDays = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

  // Etiquetas de horario: horas reales, sin nombre
  const horarios = reglas.horariosEjecucion || [];
  const scheduleLabels =
    horarios.length > 0
      ? horarios.map((h, i) => {
          if (h.horaInicio && h.horaFin) return `${h.horaInicio}\n${h.horaFin}`;
          if (h.horaInicio) return h.horaInicio;
          return `T${i + 1}`;
        })
      : ["M", "T", "N"];

  // Chunks de 3 días por página
  const chunks = [];
  for (let i = 0; i < activeDays.length; i += MAX_DIAS_POR_PAGINA) {
    chunks.push(activeDays.slice(i, i + MAX_DIAS_POR_PAGINA));
  }

  return chunks.map((chunk, chunkIdx) =>
    buildChunkPage(chunk, proceso, scheduleLabels, programa, empresa, logoBase64, chunkIdx, chunks.length)
  );
}

const checklistCalidadPdf = ({ programa, procesos, empresa }) => {
  const logoBase64 = empresa?.logo?.base64Completa || null;

  const allPages = (procesos || []).flatMap((proc) =>
    buildChecklistSection(proc, programa, empresa, logoBase64)
  );

  const content =
    allPages.length > 0
      ? allPages.map((page, i) => (i === 0 ? page : { ...page, pageBreak: "before" }))
      : [{ text: "Sin procesos registrados.", style: "textoChico" }];

  const images = logoBase64 ? { logo: logoBase64 } : {};

  return {
    info: { title: `Checklist - ${programa?.nombre || "Sin Nombre"}` },
    pageSize: "LEGAL",
    pageOrientation: "landscape",
    pageMargins: [20, 30, 20, 30],
    content,
    ...(Object.keys(images).length > 0 ? { images } : {}),
    styles: {
      textoChico: { fontSize: 7 },
      // Header
      hdrEmpresa:    { fontSize: 13, bold: true, color: "#1a1a2e" },
      hdrCheckTitle: { fontSize: 18, bold: true, color: "#2c3e50" },
      hdrProceso:    { fontSize: 9,  bold: true, color: "#34495e" },
      hdrFieldLabel: { fontSize: 6,  bold: true, color: "#888888", margin: [0, 0, 0, 1] },
      hdrFieldValue: { fontSize: 8,  color: "#222222" },
      // Grid
      gridActivityHeader: { fontSize: 8, bold: true, fillColor: "#2c3e50", color: "#ffffff" },
      gridDayHeader:      { fontSize: 10, bold: true, fillColor: "#34495e", color: "#ffffff" },
      gridSchedHeader:    { fontSize: 7,  bold: true, fillColor: "#7f8c8d", color: "#ffffff" },
      gridActivityCell:   { fontSize: 8 },
      gridDataCell:       { fontSize: 6 },
      gridSigLabel:       { fontSize: 7, bold: true, fillColor: "#ecf0f1" },
      gridSigCell:        { fontSize: 7, fillColor: "#fafafa" },
      // Observaciones
      obsLabel: { fontSize: 8, bold: true },
      obsLine:  { fontSize: 7 },
      obsFirma: { fontSize: 7 },
    },
  };
};

export default checklistCalidadPdf;
